import React, { useState } from 'react';
import { GroupSecuritySettingsModel } from '../../../../types';
import { useForm } from 'react-hook-form';
import {
  groupQueryString,
  parseQuery,
  RuleGroup,
} from '../../../shared/query-builder-utils';
import MemberRestrictionFields from '../../../shared/member-restriction-fields';
import {
  DirectoryRoles,
  isAbleToSeeUIElement,
} from '../../../utils/roles/client-roles';
import {
  saveGroupSecuritySettings,
  UserKeys,
  groupSecuritySettingsQuery,
  getDirectoryGroupQuery,
} from '../../../api/users-api';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useStore } from '../../state/zustand-store';
import ChangeLogDialog from '../../../shared/change-log-dialog';
import { Messages } from '../../../utils/message-constants';
import { handleErrors } from '../../../utils/errors';

import Box from '@mui/material/Box';
import SharedButton from '../../../shared/buttons/shared-button';
import SharedIconButton from '../../../shared/buttons/shared-icon-button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import CheckCircleOutline from '@mui/icons-material/CheckCircleOutline';
import ErrorOutline from '@mui/icons-material/ErrorOutline';
import InfoOutlined from '@mui/icons-material/InfoOutlined';

interface GroupSecuritySettingsFormProps {
  groupSecuritySettings: GroupSecuritySettingsModel;
  changeLogRoles: string[];
  userView: boolean;
  groupEmail: string;
  isLoading: boolean;
  groupId: string;
  directoryRoles: string[];
}

export default function GroupSecuritySettingsForm({
  groupSecuritySettings,
  changeLogRoles,
  userView,
  groupEmail,
  isLoading,
  groupId,
  directoryRoles,
}: GroupSecuritySettingsFormProps) {
  const { setSnackbar } = useStore();
  const queryClient = useQueryClient();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const [securityQuery, setSecurityQuery] = useState(
    groupSecuritySettings.memberRestriction
      ? groupSecuritySettings.memberRestriction.query
      : ''
  );
  const [ruleGroup, setRuleGroup] = useState<RuleGroup>(() =>
    parseQuery(securityQuery)
  );
  const {
    handleSubmit,
    formState: { isDirty },
    setValue,
  } = useForm({
    values: {
      groupSecuritySettings: groupSecuritySettings,
    },
  });

  const handleRuleChange = (updatedRuleGroup: RuleGroup) => {
    const newQueryString = groupQueryString(updatedRuleGroup);
    setValue('groupSecuritySettings.memberRestriction.query', newQueryString, {
      shouldDirty: true,
    });
    setRuleGroup(updatedRuleGroup);
  };

  const securityEvaluation = (
    settings: GroupSecuritySettingsModel
  ): {
    evaluationState: string;
    evaluationMessage: string;
    evaluationIcon: React.JSX.Element;
  } => {
    if (settings.memberRestriction) {
      switch (settings.memberRestriction.evaluation.state) {
        case 'COMPLIANT':
          return {
            evaluationState: 'COMPLIANT',
            evaluationMessage:
              'Group members must satisfy the group’s restrictions and non-compliant members can’t be added to the group.',
            evaluationIcon: (
              <CheckCircleOutline sx={{ color: 'success.main', mr: 1 }} />
            ),
          };
        case 'FORWARD_COMPLIANT':
          return {
            evaluationState: 'FORWARD COMPLIANT',
            evaluationMessage:
              'Some group members don’t satisfy the group’s restrictions and non-compliant members can’t be added to the group.',
            evaluationIcon: (
              <InfoOutlined sx={{ color: 'warning.main', mr: 1 }} />
            ),
          };
        case 'EVALUATING':
          return {
            evaluationState: 'EVALUATING',
            evaluationMessage: 'Member restriction query will be evaluated.',
            evaluationIcon: (
              <InfoOutlined sx={{ color: 'warning.main', mr: 1 }} />
            ),
          };
        case 'NON_COMPLIANT':
          return {
            evaluationState: 'NON COMPLIANT',
            evaluationMessage:
              'Group members must satisfy the group’ restrictions and non-compliant members can’t be added to the group.',
            evaluationIcon: (
              <ErrorOutline sx={{ color: 'error.main', mr: 1 }} />
            ),
          };
        default:
          return {
            evaluationState: 'NO SECURITY SETTINGS',
            evaluationMessage:
              'Group members must satisfy the group’ restrictions and non-compliant members can’t be added to the group.',
            evaluationIcon: <InfoOutlined sx={{ color: 'info.main', mr: 1 }} />,
          };
      }
    } else {
      return {
        evaluationState: 'NO SECURITY SETTINGS',
        evaluationMessage:
          'Group members must satisfy the group’ restrictions and non-compliant members can’t be added to the group.',
        evaluationIcon: <InfoOutlined sx={{ color: 'info.main', mr: 1 }} />,
      };
    }
  };

  const { mutate, isPending } = useMutation({
    mutationFn: (data: GroupSecuritySettingsModel) =>
      saveGroupSecuritySettings(data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [UserKeys.GroupSecuritySettings, groupEmail],
      });
      setChangeLogMessage('');
      setSnackbar({
        open: true,
        severity: 'success',
        message: Messages.Success.Updated('Group Security Settings'),
      });
    },
    onError: (error) => {
      queryClient.invalidateQueries({
        queryKey: [UserKeys.GroupSecuritySettings, groupEmail],
      });
      handleErrors({
        error,
        snackbarMessage: Messages.Errors.Network.Updating(
          'Group Security Settings'
        ),
      });
    },
    onSettled: () => {
      setOpenChangeLogDialog(false);
    },
  });

  const onSubmit = () => {
    const currentQueryString = groupQueryString(ruleGroup);
    const payload: GroupSecuritySettingsModel = {
      ...groupSecuritySettings,
      groupEmail,
      name: groupSecuritySettings.name,
      memberRestriction: {
        ...groupSecuritySettings.memberRestriction,
        query: currentQueryString,
      },
    };
    if (
      !userView &&
      isAbleToSeeUIElement(
        changeLogRoles,
        DirectoryRoles.EditGroupSecuritySettings.value
      )
    ) {
      setSecurityQuery(currentQueryString);
      setOpenChangeLogDialog(true);
    } else {
      mutate(payload);
    }
  };

  const evaluation = securityEvaluation(groupSecuritySettings);
  const canEdit = isAbleToSeeUIElement(
    directoryRoles,
    DirectoryRoles.EditGroupSecuritySettings.value
  );

  return (
    <>
      <Box
        component="form"
        sx={{
          position: 'relative',
          height: '100%',
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
        }}
        onSubmit={handleSubmit(onSubmit)}
      >
        <Stack
          direction="row"
          spacing={1}
          sx={{ position: 'absolute', top: -60, right: 0 }}
        >
          <SharedIconButton
            variant="refresh"
            tooltipLabel="Refresh"
            onClick={() => {
              queryClient.invalidateQueries({
                queryKey: getDirectoryGroupQuery(groupEmail).queryKey,
              });
              queryClient.invalidateQueries({
                queryKey: groupSecuritySettingsQuery(groupEmail, groupId)
                  .queryKey,
              });
            }}
          />
          <SharedButton
            variant="primary"
            disabled={!isDirty || isPending || isLoading || !canEdit}
            loading={isPending}
            type="submit"
          >
            Save
          </SharedButton>
        </Stack>
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
            overflow: 'auto',
            margin: '8px 0',
          }}
        >
          <Box>
            <Typography sx={{ fontSize: 14, fontWeight: 500, marginTop: 2 }}>
              Evaluation state
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', pt: 1, pl: 4 }}>
              {evaluation.evaluationIcon}
              <Typography variant="body2" sx={{ fontWeight: 300 }}>
                {evaluation.evaluationState}
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ fontWeight: 300, pt: 2, pl: 4 }}>
              {evaluation.evaluationMessage}
            </Typography>
          </Box>
          <MemberRestrictionFields
            ruleGroup={ruleGroup}
            onRuleGroupChange={handleRuleChange}
            disabled={!canEdit}
            disabledTooltip={Messages.Errors.Permission.Acting(
              DirectoryRoles.EditGroupSecuritySettings.name
            )}
          />
        </Box>
      </Box>

      {openChangeLogDialog && (
        <ChangeLogDialog
          open={openChangeLogDialog}
          handleClose={() => setOpenChangeLogDialog(false)}
          changeLogMessage={changeLogMessage}
          setChangeLogMessage={setChangeLogMessage}
          mutate={() => {
            const newSecuritySetting: GroupSecuritySettingsModel = {
              ...groupSecuritySettings,
              memberRestriction: {
                ...groupSecuritySettings.memberRestriction,
                query: securityQuery,
              },
              changeLogMessage,
              groupEmail,
            };
            mutate(newSecuritySetting);
          }}
          mutateLoading={isPending}
        />
      )}
    </>
  );
}
